import { type FC, type ReactNode, useState } from "react";

import { cx } from "@emotion/css";
import { Popover, PopoverPlacementTypes } from "@snapchat/snap-design-system";
import { ChevronDownIcon } from "@snapchat/snap-design-system-icons";
import { useRecoilValue } from "recoil";

import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import { organizationsSelector } from "../../../core/recoil/organizations";
import { CofConfigId, ResourceKind } from "../../../gql-generated/types";
import {
    useOrgChildResources,
    useSelectedLensCentralOrganizationNotIncludesCamkitOrgs,
    useSelectedLensFolder,
    useSelectedLensFolderV3,
} from "../../hooks";
import { PopoverContent, TextContainer, chevronIcon, chevronIconFlipped, popoverStyle, truncate } from "../../styles";
import { Flexbox } from "../Flexbox.styles";

import { FolderSearchList } from "./FolderSearchList";
import { FolderSearchListV2 } from "./FolderSearchListV2";

interface FolderSelectionDropdownProps {
    headerChildren?: ReactNode;
    fallbackText?: ReactNode;
}

export const FolderSelectionDropdown: FC<FolderSelectionDropdownProps> = ({ headerChildren, fallbackText }) => {
    const selectedOrganizationV3 = useSelectedLensCentralOrganizationNotIncludesCamkitOrgs();
    const selectedLensFolderV3 = useSelectedLensFolderV3();

    const { organizationsIndex: organizationsIndexV1, containersIndex } = useRecoilValue(organizationsSelector);
    const organization = selectedOrganizationV3 ? organizationsIndexV1.get(selectedOrganizationV3.id) : null;
    const [visible, setVisible] = useState(false);
    const [searchText, setSearchText] = useState("");
    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    const { resourceContainers: lensFolders, loading: isLensFoldersLoading } = useOrgChildResources({
        kind: ResourceKind.LensSpaces,
    });

    const { lensFolder: selectedLensFolderV2 } = useSelectedLensFolder();

    const selectedLensFolder = isMyLensesOrgsV2Enabled ? selectedLensFolderV2 : selectedLensFolderV3;

    if (isLensFoldersLoading) {
        return null;
    }

    return (
        <Flexbox className="folder-selection" data-testid="folder-selection">
            <Popover
                data-testid="folder-selection__dropdown"
                trigger="click"
                placement={PopoverPlacementTypes.BOTTOM_LEFT}
                className={popoverStyle}
                visible={visible}
                onVisibleChange={setVisible}
                content={
                    isMyLensesOrgsV2Enabled ? (
                        <>
                            <FolderSearchListV2
                                searchText={searchText}
                                setSearchText={setSearchText}
                                lensFoldersList={lensFolders}
                                headerChildren={headerChildren}
                            />
                        </>
                    ) : (
                        <>
                            <FolderSearchList
                                searchText={searchText}
                                setSearchText={setSearchText}
                                lensFoldersList={organization?.lensSpacesList ?? []}
                                containersIndex={containersIndex}
                                headerChildren={headerChildren}
                            />
                        </>
                    )
                }
            >
                <PopoverContent>
                    <TextContainer className={truncate}>{selectedLensFolder?.name ?? fallbackText}</TextContainer>
                    <ChevronDownIcon size={16} className={cx(chevronIcon, visible && chevronIconFlipped)} />
                </PopoverContent>
            </Popover>
        </Flexbox>
    );
};
